import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BellRingingIcon,
  ChartLineUpIcon,
  CopySimpleIcon,
  RankingIcon,
  UserPlusIcon,
  WarningIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardEyebrow,
  CardHeader,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';

import {
  addCopyFollow,
  getCopyLeader,
  type CopyLeaderDetail,
} from '../api';
import { useAuth } from '../auth';
import { useT } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';

function short(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function fmtUsdCompact(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtAgo(tsSec: number, t: ReturnType<typeof useT>): string {
  const diff = Date.now() / 1000 - tsSec;
  if (diff < 60) return t('copy.leader.just_now');
  if (diff < 3600) return t('copy.leader.mins_ago', { n: String(Math.floor(diff / 60)) });
  if (diff < 86400) return t('copy.leader.hours_ago', { n: String(Math.floor(diff / 3600)) });
  return t('copy.leader.days_ago', { n: String(Math.floor(diff / 86400)) });
}

export default function CopyLeader() {
  const { address = '' } = useParams();
  const t = useT();
  const auth = useAuth();
  const navigate = useNavigate();
  useTelegramBackButton();
  useNavStackEntry({ label: t('copy.leader.breadcrumb') });

  const [detail, setDetail] = useState<CopyLeaderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addMax, setAddMax] = useState('');
  const [addMult, setAddMult] = useState('');
  const [following, setFollowing] = useState(false);
  const [followError, setFollowError] = useState<string | null>(null);

  const ready = auth.state === 'ready';

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCopyLeader(address);
      setDetail(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    if (!ready) return;
    void reload();
  }, [ready, reload]);

  const onFollow = useCallback(
    async (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      setFollowError(null);
      setFollowing(true);
      try {
        const maxN = addMax.trim() === '' ? undefined : Number(addMax);
        const multN = addMult.trim() === '' ? undefined : Number(addMult);
        await addCopyFollow({
          leaderAddress: address,
          ...(maxN !== undefined && Number.isFinite(maxN)
            ? { maxUsdPerTrade: maxN }
            : {}),
          ...(multN !== undefined && Number.isFinite(multN)
            ? { sizeMultiplier: multN }
            : {}),
          ...(detail?.pseudonym ? { pseudonym: detail.pseudonym } : {}),
          ...(detail?.name ? { name: detail.name } : {}),
          ...(detail?.profileImage ? { profileImage: detail.profileImage } : {}),
        });
        navigate('/copy');
      } catch (err) {
        setFollowError((err as Error).message);
      } finally {
        setFollowing(false);
      }
    },
    [address, addMax, addMult, detail, navigate],
  );

  const eyebrow = useMemo(
    () => (
      <span className="inline-flex items-center gap-1.5">
        <RankingIcon weight="fill" className="size-3" />
        {t('copy.leader.eyebrow')}
      </span>
    ),
    [t],
  );

  if (auth.state === 'loading' || (loading && detail === null)) {
    return (
      <AuthLoadingScreen
        title={t('copy.leader.loading_title')}
        description={t('copy.leader.loading_desc')}
        shortDescription={t('copy.leader.loading_short')}
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: t('copy.leader.eyebrow'), color: 'bg-primary', delay: '0.4s' },
        ]}
      />
    );
  }

  if (error || detail === null) {
    return (
      <ScreenShell eyebrow={eyebrow} title={t('copy.leader.title')}>
        <NavBar />
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('copy.leader.load_failed')}</AlertTitle>
          <AlertDescription>{error ?? t('copy.leader.not_found')}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  const label = detail.pseudonym || detail.name || short(detail.address);

  return (
    <ScreenShell eyebrow={eyebrow} title={t('copy.leader.title')}>
      <NavBar />

      <Card>
        <CardContent className="flex items-center gap-3 pt-5">
          {detail.profileImage ? (
            <img
              src={detail.profileImage}
              alt=""
              className="size-12 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span className="bg-primary/15 text-primary flex size-12 shrink-0 items-center justify-center rounded-full text-base font-bold">
              {label.slice(0, 2).toUpperCase()}
            </span>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-foreground truncate text-base font-semibold">
              {label}
            </span>
            <button
              type="button"
              onClick={() => {
                if (typeof navigator !== 'undefined' && navigator.clipboard) {
                  void navigator.clipboard.writeText(detail.address);
                }
              }}
              className="text-muted-foreground hover:text-primary inline-flex w-fit items-center gap-1 font-mono text-[11px] transition-colors"
            >
              {short(detail.address)}
              <CopySimpleIcon weight="bold" className="size-3 opacity-60" />
            </button>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {detail.rank != null && (
                <Badge tone="primary">#{detail.rank}</Badge>
              )}
              <Badge tone="info">
                {t('copy.top_volume', { vol: fmtUsdCompact(detail.volumeUsd) })}
              </Badge>
              {detail.pnlUsd != null && (
                <Badge tone={detail.pnlUsd >= 0 ? 'success' : 'danger'}>
                  {t('copy.top_pnl', { pnl: fmtUsdCompact(detail.pnlUsd) })}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Alert tone="info">
        <BellRingingIcon weight="fill" className="size-4" />
        <AlertTitle>{t('copy.leader.how_title')}</AlertTitle>
        <AlertDescription>{t('copy.leader.how_desc')}</AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardEyebrow>
            <UserPlusIcon weight="fill" className="size-3" />
            {t('copy.leader.follow_title')}
          </CardEyebrow>
        </CardHeader>
        <CardContent>
          <form onSubmit={onFollow} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
                  {t('copy.add_max_label')}
                </span>
                <Input
                  value={addMax}
                  onChange={(e) => setAddMax(e.target.value)}
                  type="number"
                  min="1"
                  step="1"
                  inputMode="decimal"
                  placeholder="50"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
                  {t('copy.add_mult_label')}
                </span>
                <Input
                  value={addMult}
                  onChange={(e) => setAddMult(e.target.value)}
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="1"
                />
              </label>
            </div>
            {followError && (
              <Alert tone="danger">
                <WarningIcon weight="fill" className="size-4" />
                <AlertTitle>{t('copy.add_error_title')}</AlertTitle>
                <AlertDescription>{followError}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" disabled={following}>
              <BellRingingIcon weight="bold" className="size-4" />
              {following ? t('copy.adding') : t('copy.leader.follow_btn')}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardEyebrow>
            <ChartLineUpIcon weight="fill" className="size-3" />
            {t('copy.leader.recent_title')}
          </CardEyebrow>
        </CardHeader>
        <CardContent>
          {detail.recentTrades.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t('copy.leader.recent_empty')}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {detail.recentTrades.map((trade, i) => (
                <div
                  key={`${trade.slug}-${trade.timestamp}-${i}`}
                  className="bg-card/90 ring-foreground/10 flex items-start gap-2.5 rounded-2xl p-3 ring-1"
                >
                  <span
                    className={
                      trade.side === 'BUY'
                        ? 'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500'
                        : 'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-500'
                    }
                  >
                    {trade.side === 'BUY' ? (
                      <ArrowUpIcon weight="bold" className="size-3.5" />
                    ) : (
                      <ArrowDownIcon weight="bold" className="size-3.5" />
                    )}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-foreground truncate text-[13px] font-medium">
                      {trade.market || trade.slug}
                    </span>
                    <span className="text-muted-foreground text-[11px]">
                      {trade.outcome} · {(trade.price * 100).toFixed(0)}¢ ·{' '}
                      {fmtUsdCompact(trade.sizeUsd)} · {fmtAgo(trade.timestamp, t)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </ScreenShell>
  );
}
